import { i18n } from "../../translate/i18n";

/**
 * Generates a sequential name for a new node based on existing nodes of the same type.
 * Verify the highest number used in existing names and increments it.
 * 
 * @param {string} type - The type of the node (e.g., 'text', 'img').
 * @param {Array} nodes - The list of current nodes in the flow.
 * @returns {string} - The generated name (e.g., 'Texto 1', 'Imagen 3').
 */
export const generateNodeName = (type, nodes) => {
    const prefixes = {
        text: i18n.t("flowBuilder.editor.nodes.text.name"),
        message: i18n.t("flowBuilder.editor.nodes.text.name"),
        img: i18n.t("flowBuilder.editor.nodes.image.name"),
        audio: i18n.t("flowBuilder.editor.nodes.audio.name"),
        video: i18n.t("flowBuilder.editor.nodes.video.name"),
        doc: i18n.t("flowBuilder.editor.nodes.doc.name"),
        menu: i18n.t("flowBuilder.editor.nodes.menu.name"),
        input: i18n.t("flowBuilder.editor.nodes.input.name"),
        jump: i18n.t("flowBuilder.editor.nodes.jump.title") || "Saltar a",
        condition: i18n.t("flowBuilder.editor.nodes.condition.name"),
        interval: i18n.t("flowBuilder.editor.nodes.interval.name"),
        ticket: i18n.t("flowBuilder.editor.nodes.ticket.name") || "Cambio de Fila",
        variable: i18n.t("flowBuilder.editor.nodes.variable.name") || "Variable",
        regex: i18n.t("flowBuilder.editor.nodes.regex.name") || "Extracción Regex",
        gemini: i18n.t("flowBuilder.editor.nodes.gemini.name") || "Gemini AI",
        typebot: i18n.t("flowBuilder.editor.nodes.typebot.name") || "Typebot",
        openai: i18n.t("flowBuilder.editor.nodes.openai.name") || "Asistente de IA",
        tag: i18n.t("flowBuilder.editor.nodes.tag.name") || "Añadir Etiqueta",
        removeTag: i18n.t("flowBuilder.editor.nodes.removeTag.name") || "Quitar Etiqueta",
        singleBlock: i18n.t("flowBuilder.editor.nodes.content.name"),
        randomizer: i18n.t("flowBuilder.editor.nodes.random.name"),
        switchFlow: i18n.t("flowBuilder.editor.nodes.switchFlow.name") || "Cambiar Flujo",
        attendant: i18n.t("flowBuilder.editor.nodes.attendant.name") || "Asistente",
        httpRequest: i18n.t("flowBuilder.editor.nodes.httpRequest.name"),
        conditionCompare: i18n.t("flowBuilder.conditionCompare.title") || "Condiciones",
        paymentValidation: i18n.t("flowBuilder.editor.nodes.paymentValidation.name") || "Reconocimiento Pago",
        kanban: i18n.t("flowBuilder.kanban.name"),
        scheduling: i18n.t("flowBuilder.editor.nodes.scheduling.name") || "Agendamiento",
        buttonInteractive: i18n.t("flowBuilder.editor.nodes.buttonInteractive.name") || i18n.t("flowBuilder.interactiveButtons.name"),
        listInteractive: i18n.t("flowBuilder.editor.nodes.listInteractive.name") || i18n.t("flowBuilder.interactiveList.name"),
        carouselInteractive: "Carrusel",
        poll: i18n.t("flowBuilder.editor.nodes.poll.name") || "Encuesta",
        form: i18n.t("flowBuilder.editor.nodes.form.name") || "Formulario Web",
        notifier: i18n.t("flowBuilder.editor.nodes.notifier.name") || "Notificador WhatsApp",
        saveContact: i18n.t("flowBuilder.editor.nodes.saveContact.name") || "Guardar Contacto",
        listMatch: i18n.t("flowBuilder.editor.nodes.listMatch.name") || "Búsqueda en Lista",
        retargeting: i18n.t("flowBuilder.editor.nodes.retargeting.name") || "Retargeting",
        ticketStatus: i18n.t("flowBuilder.editor.nodes.ticketStatus.name") || "Estado de Ticket",
        remarketing: i18n.t("flowBuilder.editor.nodes.remarketing.name") || "Remarketing"
    };

    const prefix = prefixes[type] || "Nodo";

    // Filter nodes of the same type (or logical group)
    // We check if the name starts with the prefix to detect sequence
    // This handles cases where user renamed "Texto 1" to "Bienvenida", we shouldn't count "Bienvenida" as "Texto X"
    // BUT, we want to find the next available number for "Texto X".

    // Strategy: Find all nodes whose name matches "Prefix N" regex.
    const regex = new RegExp(`^${prefix} (\\d+)$`);

    let maxNum = 0;

    nodes.forEach(node => {
        if (node.type === type && node.data?.name) {
            let effectiveName = node.data.name;
            // Support legacy generic names matching /^Nodo\s*/i
            if (/^Nodo[\d\s]*$/i.test(effectiveName)) {
                const matchNum = effectiveName.match(/\d+/);
                if (matchNum) {
                    effectiveName = `${prefix} ${matchNum[0]}`;
                }
            }
            
            const match = effectiveName.match(regex);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxNum) maxNum = num;
            }
        }
    });

    return `${prefix} ${maxNum + 1}`;
};
